import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useCreateOrder } from "@/hooks/use-orders";
import { usePublicMenu } from "@/hooks/use-menu";
import { useCart } from "@/lib/cart-store";
import { formatPrice } from "@/lib/format-price";

export default function CheckoutPage() {
  const { t } = useTranslation();
  const { orgSlug } = useParams();
  const navigate = useNavigate();
  const cart = useCart(orgSlug);
  const { data } = usePublicMenu(orgSlug);
  const createOrder = useCreateOrder();
  const [contactName, setContactName] = useState("");
  const [contactPhone, setContactPhone] = useState("");
  const [notes, setNotes] = useState("");
  const [paymentMode, setPaymentMode] = useState<"pay_now" | "pay_later">("pay_later");

  const currency = data?.organization.currency ?? null;
  const isSupermarket = data?.organization.businessType === "supermarket";
  const payNowAvailable = Boolean(data?.ordering?.payNowAvailable);
  const orderingEnabled = Boolean(data?.ordering?.enabled);
  const effectivePaymentMode =
    paymentMode === "pay_now" && payNowAvailable ? "pay_now" : "pay_later";

  if (!orderingEnabled) {
    return (
      <div className="mx-auto flex min-h-screen w-full max-w-2xl items-center justify-center px-4 py-12">
        <div className="text-center">
          <h1 className="font-semibold text-xl">
            {isSupermarket
              ? t("menu.public.catalog.checkout.disabled", {
                  defaultValue: "Checkout no habilitado",
                })
              : t("menu.public.checkout.disabled", {
                  defaultValue: "Pedidos no habilitados",
                })}
          </h1>
        </div>
      </div>
    );
  }

  if (cart.itemCount === 0) {
    return (
      <div className="mx-auto flex min-h-screen w-full max-w-2xl items-center justify-center px-4 py-12">
        <div className="space-y-3 text-center">
          <h1 className="font-semibold text-xl">
            {isSupermarket
              ? t("menu.public.catalog.checkout.emptyTitle", {
                  defaultValue: "Tu carrito está vacío",
                })
              : t("menu.public.checkout.emptyTitle", {
                  defaultValue: "Tu pedido está vacío",
                })}
          </h1>
          <Button onClick={() => navigate(`/m/${orgSlug}`)} type="button" variant="outline">
            {isSupermarket
              ? t("menu.public.catalog.checkout.backToCatalog", {
                  defaultValue: "Volver al catálogo",
                })
              : t("menu.public.checkout.backToMenu", {
                  defaultValue: "Volver al menú",
                })}
          </Button>
        </div>
      </div>
    );
  }

  const handlePlaceOrder = async () => {
    if (!orgSlug) {
      return;
    }
    if (isSupermarket && !(contactName.trim() && contactPhone.trim())) {
      toast.error(
        t("menu.public.catalog.checkout.contactRequired", {
          defaultValue: "Ingresá nombre y teléfono para coordinar el pedido.",
        }),
      );
      return;
    }
    try {
      const result = await createOrder.mutateAsync({
        orgSlug,
        items: cart.state.lines.map((l) => ({
          productId: l.productId,
          quantity: l.quantity,
        })),
        paymentMode: effectivePaymentMode,
        contactName: contactName.trim() || null,
        contactPhone: contactPhone.trim() || null,
        notes: notes || null,
      });
      cart.clear();
      if (effectivePaymentMode === "pay_now" && result.checkoutUrl) {
        window.location.href = result.checkoutUrl;
        return;
      }
      navigate(`/m/${orgSlug}/order/${result.order.id}`);
    } catch (error) {
      toast.error(
        error instanceof Error
          ? error.message
          : t("menu.public.checkout.error", {
              defaultValue: "No se pudo crear el pedido",
            }),
      );
    }
  };

  return (
    <div className="mx-auto flex min-h-screen w-full max-w-2xl flex-col gap-6 px-4 py-12">
      <header>
        <h1 className="font-semibold font-serif text-3xl tracking-tight">
          {t("menu.public.checkout.title", {
            defaultValue: "Confirmar pedido",
          })}
        </h1>
      </header>

      <section className="space-y-2 rounded-lg border border-border bg-card p-4">
        <h2 className="font-semibold text-sm uppercase tracking-wide">
          {t("menu.public.checkout.summary", { defaultValue: "Resumen" })}
        </h2>
        <ul className="space-y-2">
          {cart.state.lines.map((line) => (
            <li className="flex items-center justify-between text-sm" key={line.productId}>
              <span>
                {line.quantity} × {line.name}
              </span>
              <span className="tabular-nums">
                {formatPrice(line.unitPrice * line.quantity, currency)}
              </span>
            </li>
          ))}
        </ul>
        <div className="flex items-center justify-between border-border border-t pt-2 font-semibold">
          <span>{t("menu.public.checkout.total", { defaultValue: "Total" })}</span>
          <span className="tabular-nums">{formatPrice(cart.subtotal, currency)}</span>
        </div>
      </section>

      <section className="space-y-3">
        <Label htmlFor="contactName">
          {t("menu.public.checkout.name", {
            defaultValue: isSupermarket ? "Tu nombre" : "Tu nombre (opcional)",
          })}
        </Label>
        <Input
          id="contactName"
          maxLength={120}
          onChange={(e) => setContactName(e.target.value)}
          required={isSupermarket}
          value={contactName}
        />

        <Label htmlFor="contactPhone">
          {t("menu.public.checkout.phone", {
            defaultValue: isSupermarket ? "Teléfono" : "Teléfono (opcional)",
          })}
        </Label>
        <Input
          id="contactPhone"
          maxLength={40}
          onChange={(e) => setContactPhone(e.target.value)}
          required={isSupermarket}
          value={contactPhone}
        />

        <Label htmlFor="notes">
          {isSupermarket
            ? t("menu.public.catalog.checkout.notes", {
                defaultValue: "Notas del pedido (opcional)",
              })
            : t("menu.public.checkout.notes", {
                defaultValue: "Notas / alergias (opcional)",
              })}
        </Label>
        <Textarea
          id="notes"
          maxLength={500}
          onChange={(e) => setNotes(e.target.value)}
          rows={3}
          value={notes}
        />
      </section>

      <section className="space-y-3">
        <h2 className="font-semibold text-sm uppercase tracking-wide">
          {t("menu.public.checkout.payment", { defaultValue: "Pago" })}
        </h2>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <button
            className={`rounded-lg border p-4 text-left ${
              effectivePaymentMode === "pay_later" ? "border-primary bg-primary/5" : "border-border"
            }`}
            onClick={() => setPaymentMode("pay_later")}
            type="button"
          >
            <div className="font-semibold text-sm">
              {isSupermarket
                ? t("menu.public.catalog.checkout.payLater", {
                    defaultValue: "Pagar al recibir",
                  })
                : t("menu.public.checkout.payLater", {
                    defaultValue: "Pagar al final",
                  })}
            </div>
            <div className="text-muted-foreground text-xs">
              {isSupermarket
                ? t("menu.public.catalog.checkout.payLaterDesc", {
                    defaultValue: "Coordinás el pago cuando se entrega el pedido.",
                  })
                : t("menu.public.checkout.payLaterDesc", {
                    defaultValue: "Pagás cuando se entrega.",
                  })}
            </div>
          </button>
          <button
            className={`rounded-lg border p-4 text-left disabled:cursor-not-allowed disabled:opacity-50 ${
              effectivePaymentMode === "pay_now" ? "border-primary bg-primary/5" : "border-border"
            }`}
            disabled={!payNowAvailable}
            onClick={() => setPaymentMode("pay_now")}
            type="button"
          >
            <div className="font-semibold text-sm">
              {t("menu.public.checkout.payNow", {
                defaultValue: "Pagar ahora",
              })}
            </div>
            <div className="text-muted-foreground text-xs">
              {payNowAvailable
                ? t("menu.public.checkout.payNowDesc", {
                    defaultValue: "Pago con Mercado Pago.",
                  })
                : t("menu.public.checkout.payNowUnavailable", {
                    defaultValue: "No disponible para este comercio.",
                  })}
            </div>
          </button>
        </div>
      </section>

      <Button
        className="w-full"
        disabled={createOrder.isPending}
        onClick={handlePlaceOrder}
        size="lg"
        type="button"
      >
        {createOrder.isPending
          ? t("menu.public.checkout.submitting", {
              defaultValue: "Enviando...",
            })
          : t("menu.public.checkout.confirm", {
              defaultValue: "Confirmar pedido",
            })}
      </Button>
    </div>
  );
}
